/**
 * LiveVideo — the actual peer video for the co-driven demo. Reuses the real,
 * battle-tested prod pieces unchanged: useWebrtcPeer (perfect negotiation over the
 * relay's rtc_signal) + VideoCallOverlay (draggable PiP). The ONLY difference from
 * the app is where creds/signaling come from: the isolated relay, never the PHI app.
 *
 * Credentials follow the prod app's therapist→client pattern: the HOST fetches BOTH
 * credential sets from the relay (x-host-key gated — anonymous minting is a free-
 * bandwidth hole) and forwards the guest's set over `rtc_ice_config`. The guest
 * never talks to the credentials endpoint.
 *
 * host  → initiator + impolite (drives the first offer)
 * guest → answerer + polite   (yields on offer glare)
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWebrtcPeer, type CallStatus } from "@/hooks/use-webrtc-peer";
import { VideoCallOverlay } from "@/components/bilateral/VideoCallOverlay";
import { getSocket } from "../../shims/socket-client";
import { getHostToken, RELAY_HTTP, type LiveRole } from "./live";

/**
 * Debug HUD — opt-in via "rtcdebug" anywhere in the URL (hash or query), so a
 * prospect on the standing Cal.com link never sees it. Surfaces the raw PC
 * states BEHIND the tile's status label: the label derives only from
 * connectionState, but media can flow while connectionState never reports
 * "connected" — the divergence between pc= and ice= on a screenshot is the
 * whole diagnosis. pointer-events-none so it can never eat a co-browse click.
 *
 * The flag is captured at MODULE LOAD, not at mount: SPA routing rewrites
 * location on every navigate (login → enter-demo lands on /dashboard), which
 * strips the hash long before LiveVideo mounts. sessionStorage keeps it across
 * a mid-call reload of a clean URL.
 */
const RTC_DEBUG = (() => {
  try {
    if (window.location.href.includes("rtcdebug")) {
      sessionStorage.setItem("rtcdebug", "1");
      return true;
    }
    return sessionStorage.getItem("rtcdebug") === "1";
  } catch {
    return false;
  }
})();
function RtcDebugHud({
  role,
  status,
  connectionState,
  iceConnectionState,
}: {
  role: LiveRole;
  status: CallStatus;
  connectionState: RTCPeerConnectionState | null;
  iceConnectionState: RTCIceConnectionState | null;
}) {
  const [log, setLog] = useState<string[]>([]);
  useEffect(() => {
    const t = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const line = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())} st=${status} pc=${connectionState ?? "-"} ice=${iceConnectionState ?? "-"}`;
    setLog((prev) => [...prev.slice(-7), line]);
  }, [status, connectionState, iceConnectionState]);
  return (
    <div
      style={{
        position: "fixed",
        bottom: 4,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 95,
        pointerEvents: "none",
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 11,
        lineHeight: 1.35,
        padding: "4px 8px",
        borderRadius: 6,
        background: "rgba(0,0,0,0.78)",
        color: "#7CFC9A",
        maxWidth: "94vw",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
    >
      <div style={{ color: "#fff" }}>
        {role} · st={status} · pc={connectionState ?? "null"} · ice={iceConnectionState ?? "null"}
      </div>
      {log.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
  );
}

export function LiveVideo({ role }: { role: LiveRole }) {
  const isHost = role === "host";
  const [iceServers, setIceServers] = useState<RTCIceServer[] | undefined>(undefined);
  const [credError, setCredError] = useState<string | null>(null);
  const guestIceRef = useRef<RTCIceServer[] | null>(null);

  // HOST: fetch both credential sets, keep ours, relay the guest's — now and again
  // on every sync_request (so a late/reloaded guest still gets its set).
  useEffect(() => {
    if (!isHost) return;
    let cancelled = false;
    const socket = getSocket();
    const relayGuestIce = () => {
      if (guestIceRef.current) socket.emit("rtc_ice_config", { iceServers: guestIceRef.current });
    };
    (async () => {
      try {
        const res = await fetch(`${RELAY_HTTP}/demo-rtc/credentials`, {
          headers: { "x-host-token": getHostToken() ?? "" },
        });
        if (!res.ok) throw new Error(`creds ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setIceServers(data.iceServers ?? []);
        guestIceRef.current = data.client?.iceServers ?? [];
        relayGuestIce(); // guest may already be waiting in the room
      } catch {
        if (!cancelled) setCredError("Could not reach the video relay.");
      }
    })();
    socket.on("sync_request", relayGuestIce);
    return () => {
      cancelled = true;
      socket.off("sync_request", relayGuestIce);
    };
  }, [isHost]);

  // GUEST: credentials arrive over the room, relayed by the host.
  useEffect(() => {
    if (isHost) return;
    const socket = getSocket();
    const onIceConfig = (payload: { iceServers?: RTCIceServer[] }) => {
      // Only accept a NON-EMPTY set — an early/empty relay (host creds still in
      // flight) would otherwise build the PC with no STUN/TURN, which can connect
      // via host candidates on the same LAN but drops the instant a direct path
      // isn't available and never relays. Stay "waiting" until real creds arrive.
      const servers = payload?.iceServers;
      if (Array.isArray(servers) && servers.length > 0) setIceServers(servers);
    };
    socket.on("rtc_ice_config", onIceConfig);
    return () => {
      socket.off("rtc_ice_config", onIceConfig);
    };
  }, [isHost]);

  const {
    localStream,
    remoteStream,
    status,
    connectionState,
    iceConnectionState,
    error,
    retry,
    switchCamera,
    endCall,
  } = useWebrtcPeer({
    enabled: iceServers !== undefined,
    isInitiator: isHost,
    isPolite: !isHost,
    iceServers,
  });

  const rtcDebug = RTC_DEBUG;

  // z-[90]: ABOVE the LiveControlBar (z-80) — the overlay's own maximized title bar
  // (with "Exit full screen") must stay clickable, or maximizing becomes a trap.
  // In normal PiP mode the window sits in a corner, so it never covers the bar.
  return createPortal(
    <div className="relative z-[90]">
      {rtcDebug && (
        <RtcDebugHud
          role={role}
          status={status}
          connectionState={connectionState}
          iceConnectionState={iceConnectionState}
        />
      )}
      <VideoCallOverlay
        localStream={localStream}
        remoteStream={remoteStream}
        status={status}
        error={error ?? credError}
        onEndCall={endCall}
        onRetry={retry}
        onSwitchCamera={switchCamera}
        // Both screens park the video bottom-right: bottom-left is the tour launcher's
        // corner, and either person can open it now.
        corner="bottom-right"
        peerLabel={isHost ? "your guest" : "your guide"}
      />
    </div>,
    document.body,
  );
}
